import { VideoCameraOutlined } from "@ant-design/icons"
import { Button, Checkbox, Form, Input, type UploadProps } from "antd"
import { type UploadFile } from "antd/lib"
import { type Store } from "antd/lib/form/interface"
import { type BaseOptionType } from "antd/lib/select"
import { type UploadChangeParam } from "antd/lib/upload"
import dayjs from "dayjs"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { AddressAutoComplete } from "../../../../../components/AutoComplete/AddressAutoComplete"
import { AttachmentsList } from "../../../../../components/AttachmentsList"
import { CustomDatePicker } from "../../../../../components/Date&TimePickers/CustomDatePicker"
import { CustomTimePicker } from "../../../../../components/Date&TimePickers/CustomTimePicker"
import { FileUpload } from "../../../../../components/FileUpload"
import { DATE_FORMAT } from "../../../../../utils/date/date"
import { getDateSafe } from "../../../../../utils/date/get-date-safe"
import { validateFile } from "../../../../../utils/file-validation"
import {
  APPOINTMENT_DEFAULT_MINUTES,
  FOLLOW_UP_DEFAULT_MINUTES
} from "../../../../Lead/LeadWorkspacePage/schedule/schedule-durations.ts"
import type { CalendarAttachment } from "../../../types/types"
import { CalendarAddAppointmentTypeSelect } from "./CalendarAddAppointmentTypeSelect"
import { CalendarAddExternalParticipantsFormItem } from "./CalendarAddExternalParticipantsFormItem"
import { CalendarAddLeadsSelect } from "./CalendarAddLeadsSelect"
import { CalendarAddParticipantsSelect } from "./CalendarAddParticipantsSelect"
import { CalendarAddProjectsSelect } from "./CalendarAddProjectsSelect"

dayjs.extend(isSameOrBefore)

const { TextArea } = Input

const optionValue = (field: unknown) =>
  field && typeof field === "object"
    ? (field as BaseOptionType).value
    : (field as string | undefined)

// Same precedence as the calendar card subtitle: a project makes it a project
// meeting, so only a lead with no project is lead-originated. Read from the
// record as opened, never from the live fields — deriving it from the mutable
// project would retract the picker the moment someone cleared it to pick
// another one.
const isLeadOriginated = (lead: unknown, project: unknown) =>
  Boolean(optionValue(lead)) && !optionValue(project)

/** The address the picked record suggests, carried on the option. */
const suggestedAddressOf = (option: unknown) =>
  (option as { suggestedAddress?: string } | undefined)?.suggestedAddress ?? ""

const MAX_SIZE_MB = 200
const MAX_FILE_COUNT = 20
const ALLOWED_FILE_EXTENSIONS = [
  "png",
  "jpeg",
  "jpg",
  "pdf",
  "mp4",
  "mov",
  "webm"
  // "doc",
  // "docx",
  // "txt"
]

interface CalendarAddEventFormProps {
  loading: boolean
  isEdit?: boolean
  timezone: string
  onSubmit: (event: CalendarEventFormSubmission) => void
  onClose: () => void
  initialValues: Store | undefined
  lockedLead?: BaseOptionType
  submitLabel?: string
  additionalFields?: React.ReactNode
}

interface CalendarEventFormSubmission extends Record<string, unknown> {
  title: string
  type: "EVENT"
  all_day: boolean
  date: string
  start_at: string
  end_at: string
  lead?: string
  project?: string
  participants?: string[]
}

export function CalendarAddEventForm({
  initialValues,
  onClose,
  onSubmit,
  timezone,
  isEdit = false,
  loading,
  lockedLead,
  submitLabel,
  additionalFields
}: CalendarAddEventFormProps) {
  const { t } = useTranslation()
  const [eventForm] = Form.useForm()
  const category = Form.useWatch("category", eventForm)
  const inviteCustomer = Form.useWatch("invite_customer", eventForm)
  // A follow-up is a reminder to act, not a meeting: it takes a moment in the
  // day, so it asks for one time and books the same block a reminder does.
  const isFollowUp = category === "FOLLOW_UP"

  // Fixed when the form opens: an edit must not change what the meeting is.
  const openedFromLead =
    Boolean(lockedLead) ||
    isLeadOriginated(initialValues?.lead, initialValues?.project)

  const [uploadedFiles, setUploadedFiles] = useState<UploadFile<any>[]>([])
  const [deletedFiles, setDeletedFiles] = useState<string[]>([])

  // What this form last put in Location itself. Anything else in the field is
  // the user's, and picking a different record must not overwrite it — that is
  // the one mistake here they cannot undo.
  const offeredAddress = useRef<string>("")

  const offerAddress = (option: unknown) => {
    const suggested = suggestedAddressOf(option)
    const current = (eventForm.getFieldValue("address") ?? "").trim()

    if (current && current !== offeredAddress.current) return

    eventForm.setFieldValue("address", suggested || undefined)
    offeredAddress.current = suggested
  }

  // Opened from a lead, nothing is ever picked: the lead arrives already
  // chosen and its control is locked, so the picker's onChange — the only
  // thing that offered an address — never fires. Offer it once on mount
  // instead. Editing is left alone: that event's Location is already its own.
  const lockedOption = lockedLead ?? initialValues?.lead
  useEffect(() => {
    if (isEdit) return
    offerAddress(lockedOption)
  }, [isEdit, lockedOption])


  const attachments = initialValues?.attachments?.filter(
    (file: CalendarAttachment) => !deletedFiles?.includes(file?.id)
  )

  const handleModalCancel = () => {
    eventForm.resetFields()
    onClose()
  }

  const normalizeEmail = (email?: string | null) => email?.toLowerCase() || ""

  // `invite_customer` is a request to send, not a state flag: the backend mails
  // the customer every time it arrives true. Re-asserting the invite the event
  // already carries would mail them again on every unrelated edit.
  const requestsCustomerInvite = (
    inviteCustomerChecked?: boolean,
    email?: string | null
  ) => {
    if (!inviteCustomerChecked) return false
    if (!isEdit) return true

    const alreadyInvited = normalizeEmail(initialValues?.customer_invite_email)
    if (!alreadyInvited) return true

    return normalizeEmail(email) !== alreadyInvited
  }

  const handleEventSubmit = ({
    upload,
    date,
    start_at,
    end_at,
    lead,
    project,
    participants,
    invite_customer,
    customer_invite_email,
    ...values
  }: any) => {
    const baseDate = dayjs(date)

    const start = dayjs.tz(
      `${baseDate.format(DATE_FORMAT)} ${start_at.format("HH:mm")}`,
      "YYYY-MM-DD HH:mm",
      timezone
    )

    const end =
      values.category === "FOLLOW_UP"
        ? start.add(FOLLOW_UP_DEFAULT_MINUTES, "minute")
        : dayjs.tz(
            `${baseDate.format(DATE_FORMAT)} ${end_at.format("HH:mm")}`,
            "YYYY-MM-DD HH:mm",
            timezone
          )

    const params: CalendarEventFormSubmission = {
      type: "EVENT",
      all_day: false,
      date: baseDate.format(DATE_FORMAT),
      start_at: start.utc().format(),
      end_at: end.utc().format(),
      participants: participants?.map((p: any) => p?.value || p),
      file_list: upload?.map((file: any) => ({ file_type: 5, file })) || [],
      lead: optionValue(lead),
      project: optionValue(project),
      ...(deletedFiles?.length && { deleted_attachment_ids: deletedFiles }),
      ...values,
      ...(requestsCustomerInvite(invite_customer, customer_invite_email) && {
        invite_customer: true,
        ...(customer_invite_email && { customer_invite_email })
      })
    }

    onSubmit?.(params)
  }

  const beforeUpload = (file: any) => {
    return validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS
    })
  }

  const uploadFileChange = (info: UploadChangeParam<UploadFile<any>>) => {
    setUploadedFiles(info.fileList)
  }

  const onDeletedFile = (file: CalendarAttachment) => {
    setDeletedFiles((prev) => [...prev, file?.id])
  }

  const availableCountFiles =
    MAX_FILE_COUNT - (initialValues?.attachments?.length || 0)

  const uploadProps: UploadProps = {
    multiple: true,
    accept: ALLOWED_FILE_EXTENSIONS?.map((ext) => `.${ext}`).join(","),
    maxCount: availableCountFiles,
    beforeUpload,
    onDrop: (e) => {
      const files = Array.from(e?.dataTransfer?.files)

      files.forEach((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase()
        if (!ALLOWED_FILE_EXTENSIONS.includes(ext || "")) {
          files.forEach(beforeUpload)
        }
      })
    },
    onChange: uploadFileChange
  }

  return (
    <Form
      form={eventForm}
      layout="vertical"
      className={"with-default-error flex flex-col flex-1 overflow-hidden"}
      onFinish={handleEventSubmit}
      validateTrigger={["onSubmit", "onChange"]}
      initialValues={{
        invite_customer: false,
        ...initialValues,
        ...(lockedLead ? { lead: lockedLead } : {}),
        date: getDateSafe(initialValues?.date),
        start_at: getDateSafe(initialValues?.start_at),
        end_at: getDateSafe(initialValues?.end_at)
      }}
      // onKeyDown={(e) => e.key === "Enter" && eventForm.submit()}
    >
      <div className={"overflow-auto flex-1"}>
        <Form.Item
          label="Title"
          name="title"
          rules={[
            { required: true, message: "Please enter event title" },
            { min: 3, message: "Title must be at least 3 characters long" }
          ]}
        >
          <Input placeholder="Event title" size="large" />
        </Form.Item>
        <Form.Item
          label={t("appointmentType.label")}
          name="category"
          validateTrigger={["onSubmit", "onChange", "onBlur"]}
          rules={[{ required: true, message: t("appointmentType.required") }]}
        >
          <CalendarAddAppointmentTypeSelect />
        </Form.Item>
        {additionalFields}
        <Form.Item
          label="Date"
          name="date"
          rules={[{ required: true, message: "Please select date" }]}
        >
          <CustomDatePicker className="w-full" size="large" />
        </Form.Item>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="Start Time"
            name="start_at"
            rules={[
              {
                required: true,
                message: "Please select start time"
              }
            ]}
          >
            <CustomTimePicker
              className="w-full"
              size="large"
              needConfirm={false}
              onChange={(startTime) => {
                const endTime = eventForm.getFieldValue("end_at")
                if (endTime) {
                  eventForm.validateFields(["end_at"])
                  return
                }
                if (!startTime) return

                const suggested = dayjs(startTime).add(
                  APPOINTMENT_DEFAULT_MINUTES,
                  "minute"
                )

                // The picker stops at 23:45 while the form books one day, so
                // an hour later can fall off the end of it.
                if (!suggested.isSame(dayjs(startTime), "day")) return

                // The scheduled day may not contain that wall clock time at
                // all. Submit re-parses HH:mm in the account's zone, and a
                // spring-forward gap resolves 02:30 to 03:30 — saving an hour
                // the user never picked. Ask the same question submit will.
                const hourAndMinute = suggested.format("HH:mm")
                const scheduled = eventForm.getFieldValue("date")
                const onScheduledDay = dayjs.tz(
                  `${dayjs(scheduled ?? startTime).format(
                    DATE_FORMAT
                  )} ${hourAndMinute}`,
                  "YYYY-MM-DD HH:mm",
                  timezone
                )
                if (onScheduledDay.format("HH:mm") !== hourAndMinute) return

                eventForm.setFieldValue("end_at", suggested)
              }}
            />
          </Form.Item>

          {!isFollowUp && (
            <Form.Item
              label="End Time"
              name="end_at"
              // trigger={"onChange"}
              rules={[
                {
                  required: true,
                  message: "Please select end time"
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve()
                    const startTime = eventForm.getFieldValue("start_at")
                    if (!startTime) return Promise.resolve()

                    const today = dayjs().format(DATE_FORMAT)
                    const startDayjs = dayjs(
                      `${today} ${dayjs(startTime).format("HH:mm")}`
                    )
                    const endDayjs = dayjs(
                      `${today} ${dayjs(value).format("HH:mm")}`
                    )

                    if (endDayjs.isSameOrBefore(startDayjs)) {
                      return Promise.reject(
                        new Error("End time must be after start time")
                      )
                    }

                    const diffInMinutes = endDayjs.diff(startDayjs, "minute")

                    if (diffInMinutes < 15) {
                      return Promise.reject(
                        new Error("At least 15 minutes after the start time")
                      )
                    }

                    return Promise.resolve()
                  }
                }
              ]}
            >
              <CustomTimePicker
                className="w-full"
                size="large"
                needConfirm={false}
              />
            </Form.Item>
          )}
        </div>

        <Form.Item label="Link to Lead" name="lead">
          <CalendarAddLeadsSelect
            disabled={!!lockedLead}
            onChange={(value: unknown, option: unknown) => {
              // A meeting belongs to one record. Without this, choosing the
              // project first and the lead second still pairs them.
              if (!openedFromLead && optionValue(value)) {
                eventForm.setFieldValue("project", undefined)
              }
              offerAddress(option)
            }}
          />
        </Form.Item>

        {!openedFromLead &&
          (isEdit ? (
            <Form.Item label="Link to Project" name="project">
              <CalendarAddProjectsSelect
                onChange={(_: unknown, option: unknown) => offerAddress(option)}
              />
            </Form.Item>
          ) : (
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => prev.lead !== next.lead}
            >
              {({ getFieldValue }) =>
                optionValue(getFieldValue("lead")) ? null : (
                  <Form.Item label="Link to Project" name="project">
                    <CalendarAddProjectsSelect
                      onChange={(_: unknown, option: unknown) =>
                        offerAddress(option)
                      }
                    />
                  </Form.Item>
                )
              }
            </Form.Item>
          ))}

        <Form.Item
          label="Location"
          name="address"
          extra="Taken from the linked record's property address, or its customer's. Edit it for a different location."
        >
          <AddressAutoComplete
            allowClear
            size="large"
            placeholder="Street, city, state, ZIP"
            onSelect={(suggestion) => {
              // The picker's own onChange emits the street line only; a meeting
              // Location is one free-text field, so keep the whole address.
              const full =
                suggestion.address.display || suggestion.address.street
              eventForm.setFieldValue("address", full)
              // A hand-picked address is the user's, not one this form offered.
              offeredAddress.current = ""
            }}
          />
        </Form.Item>

        <Form.Item
          label="Meeting link"
          name="meeting_link"
          rules={[{ type: "url", message: "Enter a valid link (https://…)" }]}
        >
          <Input
            allowClear
            placeholder="https://zoom.us/j/…  (optional)"
            prefix={<VideoCameraOutlined />}
            size="large"
            type="url"
          />
        </Form.Item>

        <Form.Item label="Internal Participants (Team)" name="participants">
          <CalendarAddParticipantsSelect />
        </Form.Item>

        <CalendarAddExternalParticipantsFormItem />

        <Form.Item name="invite_customer" valuePropName="checked">
          <Checkbox>Invite Customer (limited meeting details only)</Checkbox>
        </Form.Item>

        {inviteCustomer && (
          <Form.Item
            label="Customer Invitation Email"
            name="customer_invite_email"
            extra="Optional when the linked lead or project already has a customer email."
            rules={[{ type: "email", message: "Enter a valid customer email" }]}
          >
            <Input
              type="email"
              placeholder="Use linked customer email when blank"
              size="large"
            />
          </Form.Item>
        )}

        {/* Attachments */}
        <Form.Item
          label="Attachments"
          name="upload"
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
          rules={[
            {
              validator: (_, value) => {
                if (value && value.length > availableCountFiles) {
                  return Promise.reject(
                    new Error("You can upload a maximum of 20 files.")
                  )
                }
                return Promise.resolve()
              }
            }
          ]}
        >
          <FileUpload
            uploadProps={uploadProps}
            files={uploadedFiles}
            options={{
              extensions: ALLOWED_FILE_EXTENSIONS,
              maxSize: MAX_SIZE_MB
            }}
          />
        </Form.Item>

        {!!attachments?.length && (
          <Form.Item>
            <AttachmentsList
              attachments={attachments}
              preview={true}
              onDelete={onDeletedFile}
            />
          </Form.Item>
        )}

        <Form.Item label="Comment" name="comment">
          <TextArea placeholder="Optional comment" rows={3} size="large" />
        </Form.Item>
      </div>

      <Form.Item className="mb-0">
        <div className="flex justify-end gap-3">
          <Button disabled={loading} size="large" onClick={handleModalCancel}>
            Cancel
          </Button>
          <Button
            loading={loading}
            size="large"
            type="primary"
            htmlType="submit"
          >
            {submitLabel ?? (isEdit ? "Save Event" : "Create Event")}
          </Button>
        </div>
      </Form.Item>
    </Form>
  )
}

export type { CalendarEventFormSubmission }
